import { useState } from 'react';
import { Loader2, Undo2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  PAYMENT_METHOD_LABELS,
  SELECTABLE_PAYMENT_METHODS,
  recordPaymentSchema,
  toCents,
  type InvoicePayment,
  type SelectablePaymentMethod,
} from '@jobos/shared';
import { useInvoicePayments, useReversePayment, useAmendPayment } from '@/hooks/useInvoicePayments';
import { formatCurrency, cn } from '@/lib/utils';

/**
 * The ledger history, exactly as recorded.
 *
 * Superseded and reversed rows are de-emphasised but NEVER hidden — that
 * visibility is the whole point of an append-only ledger (NFR-3). A correction
 * you cannot see is indistinguishable from an edit.
 *
 * A `superseded` row is what an amend leaves behind, rendered struck through
 * beside its replacement.
 */
interface PaymentHistoryListProps {
  invoiceId: string;
  jobId?: string;
  /**
   * The invoice's remaining balance, in dollars. Caps an amend at
   * `outstanding + that row's own amount` — the row is replaced, so its money
   * frees up. OPTIONAL: a caller that does not know the balance gets an
   * uncapped amend rather than one blocked by a ceiling of zero.
   */
  outstanding?: number;
}

const STATE_STYLES: Record<InvoicePayment['state'], string> = {
  active: 'text-gray-900',
  superseded: 'text-gray-400 line-through',
  reversed: 'text-gray-400 line-through',
};

const STATE_LABELS: Record<InvoicePayment['state'], string | null> = {
  active: null,
  superseded: 'Replaced',
  reversed: 'Reversed',
};

const formatDate = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
};

interface AmendDraft {
  amount: string;
  receivedOn: string;
  method: SelectablePaymentMethod;
  reference: string;
}

export function PaymentHistoryList({ invoiceId, jobId, outstanding }: PaymentHistoryListProps) {
  const { data: payments, isLoading } = useInvoicePayments(invoiceId);
  const reversePayment = useReversePayment(invoiceId, jobId);
  const amendPayment = useAmendPayment(invoiceId, jobId);

  const [reversingId, setReversingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // Exactly one correction form is open at a time — editing and reversing the
  // same row at once is two answers to one question.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AmendDraft | null>(null);
  const [amendError, setAmendError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!payments?.length) {
    return <p className="text-sm text-gray-500 py-2">No payments recorded.</p>;
  }

  // Reversal markers carry $0 and exist only to record who/when/why. Index them
  // by the row they reverse so the reason renders against that row.
  const reversalOf = new Map<string, InvoicePayment>();
  for (const p of payments) if (p.reversesId) reversalOf.set(p.reversesId, p);
  const numberOf = new Map(payments.map((p, i) => [p.id, i + 1]));

  const startReversal = (id: string) => {
    setReversingId(id);
    setReason('');
    setEditingId(null);
    setDraft(null);
    setAmendError(null);
  };

  const startEdit = (p: InvoicePayment) => {
    setReversingId(null);
    setEditingId(p.id);
    setAmendError(null);
    setDraft({
      amount: String(p.amount),
      receivedOn: p.receivedOn.slice(0, 10),
      // `external` is never selectable (FR-1.2). A migrated row carrying it
      // falls back to cheque rather than putting an unoffered value in the
      // select, which would submit something the schema rejects.
      method: (SELECTABLE_PAYMENT_METHODS as readonly string[]).includes(p.method)
        ? (p.method as SelectablePaymentMethod)
        : 'cheque',
      reference: p.reference ?? '',
    });
  };

  const closeEdit = () => {
    setEditingId(null);
    setDraft(null);
    setAmendError(null);
  };

  const submitAmend = async (original: InvoicePayment) => {
    if (!draft) return;

    const parsed = recordPaymentSchema.safeParse({
      amount: Number(draft.amount),
      receivedOn: draft.receivedOn,
      method: draft.method,
      reference: draft.reference.trim() || null,
    });
    if (!parsed.success) {
      setAmendError(
        parsed.error.issues.some((i) => i.path[0] === 'amount')
          ? 'Enter an amount greater than zero, to the cent.'
          : (parsed.error.issues[0]?.message ?? 'That correction is not valid.'),
      );
      return;
    }

    // The replacement supersedes this row, so the headroom is the invoice's
    // remaining balance plus whatever this row is currently holding.
    if (outstanding !== undefined) {
      const ceilingCents = toCents(outstanding) + toCents(original.amount);
      if (toCents(parsed.data.amount) > ceilingCents) {
        setAmendError(
          `More than the ${formatCurrency(ceilingCents / 100)} available on this invoice.`,
        );
        return;
      }
    }

    await amendPayment.mutateAsync({ paymentId: original.id, ...parsed.data });
    closeEdit();
  };

  const submitReversal = async () => {
    if (!reversingId || !reason.trim()) return;
    await reversePayment.mutateAsync({ paymentId: reversingId, reason: reason.trim() });
    setReversingId(null);
    setReason('');
  };

  return (
    <ul className="divide-y divide-gray-100">
      {payments.map((p) => {
        const reversal = reversalOf.get(p.id);
        const isMarker = !!p.reversesId;

        return (
          <li key={p.id} className="py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className={cn('flex items-center gap-2 text-sm', STATE_STYLES[p.state])}>
                  <span className="font-medium">{formatCurrency(p.amount)}</span>
                  <span className="text-gray-500">{formatDate(p.receivedOn)}</span>
                  <span className="text-gray-500">{PAYMENT_METHOD_LABELS[p.method]}</span>
                  {p.reference && <span className="text-gray-400 truncate">{p.reference}</span>}
                </div>

                <div className="mt-0.5 text-xs text-gray-500 space-x-2">
                  {STATE_LABELS[p.state] && (
                    <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                      {STATE_LABELS[p.state]}
                    </span>
                  )}
                  {p.supersedesId && (
                    <span>Replaces payment #{numberOf.get(p.supersedesId) ?? '?'}</span>
                  )}
                  {isMarker && (
                    <span>Reversal of payment #{numberOf.get(p.reversesId!) ?? '?'}</span>
                  )}
                  <span>Recorded by {p.createdByName ?? 'the migration'}</span>
                </div>

                {p.reversalReason && (
                  <p className="mt-1 text-xs text-gray-600">Reason: {p.reversalReason}</p>
                )}
                {reversal?.reversalReason && !p.reversalReason && (
                  <p className="mt-1 text-xs text-gray-600">
                    Reversed: {reversal.reversalReason}
                  </p>
                )}
              </div>

              {/* Both corrections apply to active money rows only: never to one
                  already superseded or reversed, never to a $0 marker.

                  Edit is an amend — a mistyped part payment is corrected by
                  superseding the row, not by editing it. Reverse is for money
                  that never arrived, or arrived against the wrong invoice. */}
              {p.state === 'active' && !isMarker && (
                <div className="flex shrink-0 gap-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(p)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => startReversal(p.id)}>
                    <Undo2 className="h-3.5 w-3.5 mr-1" />
                    Reverse
                  </Button>
                </div>
              )}
            </div>

            {editingId === p.id && draft && (
              <div className="mt-2 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor={`amend-amount-${p.id}`}
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Amount received
                    </label>
                    <Input
                      id={`amend-amount-${p.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={draft.amount}
                      onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`amend-date-${p.id}`}
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Date received
                    </label>
                    <Input
                      id={`amend-date-${p.id}`}
                      type="date"
                      value={draft.receivedOn}
                      onChange={(e) => setDraft({ ...draft, receivedOn: e.target.value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`amend-method-${p.id}`}
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      How was this paid?
                    </label>
                    <select
                      id={`amend-method-${p.id}`}
                      value={draft.method}
                      onChange={(e) =>
                        setDraft({ ...draft, method: e.target.value as SelectablePaymentMethod })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {SELECTABLE_PAYMENT_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {PAYMENT_METHOD_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor={`amend-reference-${p.id}`}
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Reference <span className="text-gray-400">(optional)</span>
                    </label>
                    <Input
                      id={`amend-reference-${p.id}`}
                      value={draft.reference}
                      placeholder="Check number, transaction id…"
                      onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
                    />
                  </div>
                </div>

                {amendError && <p className="text-sm text-red-600">{amendError}</p>}

                {/* Said plainly, because it is the part people get wrong: the
                    original row does not disappear. */}
                <p className="text-xs text-gray-500">
                  The original stays in the history, struck through, with this correction beneath it.
                </p>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={closeEdit}
                    disabled={amendPayment.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void submitAmend(p)}
                    disabled={amendPayment.isPending}
                  >
                    {amendPayment.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                    Save correction
                  </Button>
                </div>
              </div>
            )}

            {reversingId === p.id && (
              <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                <label htmlFor={`reason-${p.id}`} className="block text-xs font-medium text-gray-700 mb-1">
                  Why is this being reversed?
                </label>
                <Textarea
                  id={`reason-${p.id}`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Check bounced, recorded against the wrong invoice…"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setReversingId(null)}
                    disabled={reversePayment.isPending}
                  >
                    Cancel
                  </Button>
                  {/* A reversal without a stated reason is an unexplained hole
                      in the ledger, so the button stays disabled until typed. */}
                  <Button
                    type="button"
                    size="sm"
                    onClick={submitReversal}
                    disabled={!reason.trim() || reversePayment.isPending}
                  >
                    {reversePayment.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                    Reverse payment
                  </Button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
